/**
 * Version-agnostic core for the `query` router mode.
 *
 * The embed historically lived behind a hashbang (`#!/e/slug`), which no crawler
 * can distinguish. Query mode moves the inner route into the real, server-visible
 * query string so each experience gets a distinct URL.
 *
 * This module is intentionally free of any `react-router` / `history` import: it
 * only maps between an internal route (`pathname` + `search`) and a flat query
 * string. The thin, version-specific history adapter lives in `createQueryHistory`
 * and delegates all encoding/decoding here, so a future react-router upgrade only
 * swaps the adapter and keeps this logic (and its tests) untouched.
 *
 * Scheme (see the plan's "URL scheme" section): the first path segment picks a
 * clean public key; the value carries the rest of the path. Routes whose first
 * segment has no key (or no id after it) fall back to the generic `view` param. The
 * route's own params (`date`, `time`, …) ride alongside as ordinary query params,
 * and any foreign params (attribution/tracking) are preserved untouched.
 */

/**
 * Public param key → the internal first path segment it stands in for.
 *
 * The public keys are deliberately camelCased (`experienceId`, not `experience`),
 * NOT bare domain nouns. A partner's CMS (WordPress, etc.) registers singular
 * lowercase nouns like `experience`/`event` as custom-post-type / taxonomy query
 * vars, and a deep-linked `…?experience=<slug>` then makes the host run a query for
 * a non-existent post and serve its own 404 before our embed ever mounts — so the
 * link works in-app (pushState, no server round-trip) but 404s when pasted/reloaded.
 * The camelCase form sidesteps that collision class (a CMS never registers a
 * camelCase query var), while the keyword still lives in the slug VALUE for SEO.
 */
const KEY_TO_SEGMENT = {
  experienceId: 'e',
  collectionId: 'collection',
  catalogId: 'catalogs',
  // `cartsId`, not `cartId`, because `cartId` is an app-owned param (see
  // APP_PARAM_KEYS): a key that is BOTH a route key and an app param breaks
  // encodeRoute's routing-by-set-membership. The plural mirrors the `carts` segment.
  cartsId: 'carts',
  eventId: 'event',
  addonsId: 'addons',
  landingId: 'landing',
} as const;

/**
 * The carrier for every keyless route — a page with no id to name: the browse views
 * (`/calendar`, `/search`) and the in-flow / terminal pages (`/checkout`,
 * `/join-waitlist`, confirmations). The value is the page's own pathname (sans
 * leading slash), so `/calendar` → `?view=calendar` and `/checkout` → `?view=checkout`.
 * One readable, shareable label for all of them — a bare `?calendar` has no value and
 * is ambiguous with an app param. Product pages that carry an id use their own key
 * instead (`?experienceId=<slug>`); see {@link KEY_TO_SEGMENT}.
 */
export const VIEW_PARAM = 'view';

/** Every valid route param name: a product key, or the generic `view` carrier. */
export type RouteKey = keyof typeof KEY_TO_SEGMENT | typeof VIEW_PARAM;

const SEGMENT_TO_KEY: Record<string, RouteKey | undefined> = Object.fromEntries(
  (Object.entries(KEY_TO_SEGMENT) as [RouteKey, string][]).map(
    ([key, segment]): [string, RouteKey] => [segment, key],
  ),
);

/**
 * Precedence when decoding: a product key wins over the generic `view` carrier.
 * Order follows the keyword table, with `view` last.
 */
const DECODE_PRECEDENCE: readonly RouteKey[] = [
  ...(Object.keys(KEY_TO_SEGMENT) as (keyof typeof KEY_TO_SEGMENT)[]),
  VIEW_PARAM,
];

/**
 * Every param name the query router owns. Callers that read the query string for
 * app purposes (e.g. preserved-search-param helpers) must exclude these so a route
 * param is never mistaken for an application param.
 */
export const ROUTE_PARAM_KEYS: readonly string[] = DECODE_PRECEDENCE;

/**
 * The params the APP itself owns — its filters, booking/session state, and the guest
 * form fields that a deep link can pre-fill. These are authoritative from the
 * navigation TARGET: present → set, absent → cleared. {@link encodeRoute} therefore
 * takes them only from the target and never carries them over from the current URL,
 * which is what makes "clear a filter" actually clear (an omitted filter is not
 * re-added) and keeps guest PII (name/email/phone) from persisting across navigation.
 *
 * Every OTHER param — attribution/tracking (`utm_*`, `gclid`, `ttclid`, `icamp`, …),
 * locale (`lang`, `language`, …), and any unknown host param (a partner's own
 * `p`/`c`/`lg`) — is NOT listed here and so rides along untouched (see encodeRoute
 * step 2). That is the inversion: preserve everything by default, strip only what the
 * app owns. Locale lands here too by being absent — it's carried from the current URL
 * unless the target sets it explicitly (an explicit language switch then wins).
 *
 * Bias: when unsure, LIST a param here. Over-listing only means a host param that
 * happens to share an app name isn't carried — no worse than before this change.
 * UNDER-listing is the real hazard: an app filter/state param left off would persist
 * when a navigation means to drop it (a stuck filter, or leaked PII).
 */
export const APP_PARAM_KEYS: readonly string[] = [
  // Search / landing / calendar filters
  'categoryIds',
  'vibeIds',
  'from',
  'to',
  'fromDate',
  'startDate',
  'endDate',
  'productLine',
  // Booking + session state
  'date',
  'time',
  'duration',
  'preselectedDate',
  'latest',
  'skipParticipantsName',
  // Guest form pre-fill (PII — must never pass through)
  'firstName',
  'lastName',
  'emailAddress',
  'phoneNumber',
  // Flow / identifiers / transient state
  'cartId',
  'waitlistId',
  'accessCode',
  'code',
  'previous',
  'debug',
];

const stripSlashes = (value: string): string =>
  value.replace(/^\/+/, '').replace(/\/+$/, '');

/**
 * Serialize params using the SAME encoding the rest of the app uses to build
 * query strings — `URLSearchParams.toString()` (form-urlencoded: space→`+`,
 * `(`→`%28`, `:`→`%3A`, …). This matters because components compare their
 * hand-built URLs against react-router's `location.search` by exact string (e.g.
 * EventPage's `location.pathname+location.search !== cleanedUrl` redirect); a
 * different encoding there loops forever. We only override `/` → literal, so the
 * route param value (`?cartsId=123/guest-reschedule`) stays readable.
 */
const serializeParams = (params: URLSearchParams): string => {
  const serialized = params.toString().replace(/%2F/g, '/');
  return serialized ? `?${serialized}` : '';
};

const toSearchString = (search: string): string =>
  search && !search.startsWith('?') ? `?${search}` : search;

interface RouteParam {
  key: RouteKey;
  value: string;
}

export interface CodecOptions {
  /**
   * When the brand's home route IS the experience list (the common case: all V3
   * brands with the new landing page, unless `data-home="calendar"`), the bare
   * `/e` list route collapses to the no-param home URL so the landing is clean
   * (`…/experiences/`, never `?view=e`). Calendar-home brands leave this false
   * so their list stays reachable at `?view=e` while the bare URL is the calendar.
   */
  listIsHome?: boolean;
}

/** The internal landing/list route that collapses to the bare home URL. */
const EXPERIENCES_LIST_SEGMENT = KEY_TO_SEGMENT.experienceId; // 'e'

/**
 * Map an internal pathname to the single route param that encodes it, or `null`
 * for a route that carries no param (the home route `/`, and — when
 * `listIsHome` — the bare experience list `/e`).
 */
export const encodePathname = (
  pathname: string,
  { listIsHome = false }: CodecOptions = {},
): RouteParam | null => {
  const trimmed = stripSlashes(pathname);
  if (!trimmed) {
    return null;
  }

  if (listIsHome && trimmed === EXPERIENCES_LIST_SEGMENT) {
    return null;
  }

  const [first, ...rest] = trimmed.split('/');
  const namedKey = SEGMENT_TO_KEY[first];

  if (namedKey && rest.length > 0) {
    return { key: namedKey, value: rest.join('/') };
  }

  // Everything else — keyless pages — rides the generic `view` carrier with the
  // pathname itself as the value (`/calendar` → view=calendar, `/checkout` → view=checkout).
  return { key: VIEW_PARAM, value: trimmed };
};

/** Reverse of {@link encodePathname}: a route param back to its internal pathname. */
export const decodePathname = (key: RouteKey, value: string): string => {
  if (key === VIEW_PARAM) {
    return `/${stripSlashes(value)}`;
  }

  const segment = KEY_TO_SEGMENT[key];
  return `/${segment}/${stripSlashes(value)}`;
};

export interface DecodedLocation {
  hash: string;
  pathname: string;
  search: string;
}

/**
 * Decode a window query string into the internal location react-router should see.
 * The matched route param is removed from `search`; every other param (the route's
 * own `date`/`time`/… plus any foreign attribution params) is passed through.
 * With no route param, the bare URL is the home route `/` (whose component the app
 * resolves per brand). The `/e`→bare collapse is an encode-only concern.
 */
export const decodeRoute = (windowSearch: string): DecodedLocation => {
  const params = new URLSearchParams(toSearchString(windowSearch));

  const matchedKey = DECODE_PRECEDENCE.find((key) => params.has(key));
  // No route param = the bare home URL, which is always `/`. Its component (the
  // experience list for new-landing brands, the calendar for data-home=calendar)
  // is chosen by the app via `getHomeComponent`, exactly as in hash mode — so the
  // landing keeps the `/` pathname that in-app navigation guards rely on.
  const pathname = matchedKey
    ? decodePathname(matchedKey, params.get(matchedKey) ?? '')
    : '/';

  if (matchedKey) {
    params.delete(matchedKey);
  }

  return { hash: '', pathname, search: serializeParams(params) };
};

/**
 * Encode a target internal location into the full window query string.
 *
 * Inversion of the old denylist: PRESERVE every current-URL param by default, and
 * strip only the params the app owns ({@link APP_PARAM_KEYS}) plus the route keys. So
 * attribution (`utm_*`, `gclid`, `ttclid`, …), locale (`lang`, …) and any unknown host
 * param (a partner's `p`/`c`/`lg`) ride along across a navigation, while the app's own
 * filters / booking / guest params come solely from the navigation target — present →
 * kept, absent → cleared. That is what keeps "clear a filter" working (an omitted
 * filter is not re-added) and stops guest PII from persisting. A value the target sets
 * overrides the carried one, so an explicit language switch still wins.
 */
export const encodeRoute = (
  target: { pathname: string; search?: string },
  currentWindowSearch: string,
  options: CodecOptions = {},
): string => {
  const current = new URLSearchParams(toSearchString(currentWindowSearch));
  const params = new URLSearchParams();

  // 1. Route param FIRST, so the URL reads path-before-query like prod's
  //    `#!/collection/<id>/group/<id>?date=…` → `?collectionId=<id>/group/<id>&date=…`.
  const routeParam = encodePathname(target.pathname, options);
  if (routeParam) {
    params.set(routeParam.key, routeParam.value);
  }

  // 2. Pass through every CURRENT param the app does not own — attribution, locale,
  //    and any unknown host param. Route keys and app-owned params are excluded so the
  //    latter come solely from the target (step 3): that is what lets a filter clear
  //    and keeps guest PII from riding along.
  current.forEach((value, key) => {
    if (!ROUTE_PARAM_KEYS.includes(key) && !APP_PARAM_KEYS.includes(key)) {
      params.append(key, value);
    }
  });

  // 3. Apply the navigation's own params — authoritative. For each non-route key the
  //    target specifies, drop any value carried over in step 2 (the target wins, and
  //    multi-value params like `categoryIds` don't accumulate across both sources),
  //    then append the target's value(s).
  const targetParams = new URLSearchParams(toSearchString(target.search ?? ''));
  const overridden = new Set<string>();
  targetParams.forEach((_value, key) => {
    if (!ROUTE_PARAM_KEYS.includes(key) && !overridden.has(key)) {
      params.delete(key);
      overridden.add(key);
    }
  });
  targetParams.forEach((value, key) => {
    if (!ROUTE_PARAM_KEYS.includes(key)) {
      params.append(key, value);
    }
  });

  return serializeParams(params);
};
